"use client";

import { memo, useEffect, useRef } from "react";
import Image from "next/image";
import { Marquee } from "./marquee";

type Tech = { id: string; label: string; img: string };

// Static data outside the component. Leading "/" on every path (relative paths
// break on nested routes), duplicate "Node js" entry removed, unused fields dropped.
const TECHS: Tech[] = [
  { id: "aws", label: "AWS", img: "/tech/imageye___-_imgi_50_aws-logo-rgb.png" },
  { id: "django", label: "Django", img: "/tech/Django--Streamline-Svg-Logos.png" },
  { id: "docker", label: "Docker", img: "/tech/Docker--Streamline-Svg-Logos.png" },
  { id: "flask", label: "Flask", img: "/tech/flask-name.svg" },
  { id: "nextjs", label: "Next.js", img: "/tech/Nextjs--Streamline-Svg-Logos(1).png" },
  { id: "typescript", label: "TypeScript", img: "/tech/Typescript--Streamline-Svg-Logos.png" },
  { id: "tailwind", label: "Tailwind CSS", img: "/tech/Tailwindcss--Streamline-Svg-Logos.png" },
  { id: "nodejs", label: "Node.js", img: "/tech/Nodejs--Streamline-Svg-Logos.png" },
  { id: "git", label: "Git", img: "/tech/imageye___-_imgi_1_logo@2x.png" },
  { id: "python", label: "Python", img: "/tech/imageye___-_imgi_1_python-logo.png" },
  { id: "fastapi", label: "FastAPI", img: "/tech/imageye___-_imgi_10_logo-teal.png" },
  { id: "mysql", label: "MySQL", img: "/tech/mysql-logo.svg" },
  { id: "nginx", label: "Nginx", img: "/tech/nginx_logo.svg" },
  { id: "wordpress", label: "WordPress", img: "/tech/Wordpress--Streamline-Svg-Logos.png" },
  { id: "postgresql", label: "PostgreSQL", img: "/tech/imageye___-_imgi_48_postgresql.png" },
  { id: "react", label: "React", img: "/tech/a9bf22bd-373a-4fae-a900-c22fd12c87c7.png" },
];

// Round-robin split keeps rows even (6 / 5 / 5), so each row is wide enough to loop.
const ROWS = [0, 1, 2].map((r) => TECHS.filter((_, i) => i % 3 === r));

const TechCard = memo(function TechCard({ img, label }: Tech) {
  return (
    <figure className="flex h-full w-32 flex-col items-center overflow-hidden rounded-xl border border-dotted bg-background p-4 lg:w-64 lg:p-8">
      <Image
        src={img}
        alt=""
        width={128}
        height={128}
        // Without `sizes`, next/image assumes 100vw and ships huge files for a 64–128px logo.
        sizes="(min-width: 640px) 128px, 64px"
        className="h-16 w-16 object-contain sm:h-32 sm:w-32"
      />
      <figcaption className="mt-1 text-center text-sm font-semibold lg:mt-2 lg:text-lg">
        {label}
      </figcaption>
    </figure>
  );
});

export function MarqueeDemo() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Pause every animation while the section is off screen. Sets a data attribute
  // directly, so scrolling never triggers a React re-render.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        el.dataset.paused = String(!entry.isIntersecting);
      },
      { rootMargin: "100px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      data-paused="true"
      className="relative mb-16 flex w-full flex-col items-center justify-center overflow-hidden bg-white data-[paused=true]:[&_*]:[animation-play-state:paused] motion-reduce:[&_*]:[animation-play-state:paused]"
    >
      <h3 className="mt-20 text-center uppercase text-primary lg:mt-40">Framework</h3>
      <h1 className="text-center text-3xl font-bold lg:text-6xl">
        A Stack Build on the best <br /> of Modern Apps
      </h1>
      <p className="mx-auto mb-16 mt-4 max-w-72 text-center text-base lg:max-w-4xl lg:text-xl">
        We use a powerful, modern technology stack to build fast, secure, scalable, and
        reliable web applications combining proven frameworks, flexible architecture, and
        industry-leading tools.
      </p>

      {ROWS.map((row, i) => (
        <Marquee
          key={i}
          reverse={i === 1}
          pauseOnHover
          repeat={3}
          className="[--duration:20s]"
        >
          {row.map((tech) => (
            <TechCard key={tech.id} {...tech} />
          ))}
        </Marquee>
      ))}

      {/* Edge fades (the original had the left one twice) */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-background" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-background" />
    </div>
  );
}